import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Archive,
  BadgeCheck,
  Crown,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Save,
  Sparkles,
  Star,
  Store as StoreIcon,
  Trash2,
  Truck,
  Eye,
  MessageSquare,
  Package,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { StoreVideoManager } from "@/components/store-video-manager";
import { StoreMediaManager } from "@/components/store-media-manager";
import { StoreOrderForm } from "@/components/store-order-form";
import { StoreOrdersList } from "@/components/store-orders-list";

import { cities, categories } from "@/lib/data";
import { azError } from "@/lib/az-errors";
import { supabase } from "@/integrations/supabase/client";
import { uploadListingPhoto, statusLabels, PLACEHOLDER_IMAGE } from "@/lib/listings";
import { fetchMyListings } from "@/lib/listings";
import {
  averageRating,
  DELIVERY_OPTIONS,
  fetchFollowerCount,
  fetchStoreReviews,
  fetchMyStore,
  saveStore,
  storeSlug,
  storeStatusLabels,
  type StoreInput,
} from "@/lib/stores";

const STORE_PLANS = [
  {
    key: "basic",
    name: "Standart",
    price: "Pulsuz",
    perks: ["Mağaza vitrini", "Əlaqə düymələri", "Məhsul idarəetməsi"],
  },
  {
    key: "premium",
    name: "Premium mağaza",
    price: "19 ₼ / ay",
    perks: ["Vitrində yuxarıda", "Qızılı Premium nişanı", "Genişləndirilmiş statistika"],
  },
  {
    key: "vip",
    name: "VIP mağaza",
    price: "39 ₼ / ay",
    perks: ["Ana səhifədə vitrin", "VIP nişan və parıltı", "Prioritet dəstək", "Rəsmi nişan sorğusu"],
  },
] as const;

export const Route = createFileRoute("/_authenticated/magazam")({
  head: () => ({
    meta: [
      { title: "Mağazam — idarəetmə | Albazar.az" },
      {
        name: "description",
        content: "Mağazanı yarat, loqo və banner yüklə, əlaqə məlumatlarını və məhsullarını idarə et.",
      },
      { property: "og:title", content: "Mağazam — idarəetmə | Albazar.az" },
      { property: "og:description", content: "Mağaza profilini və məhsullarını idarə et." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MyStorePage,
});

const EMPTY: StoreInput = {
  name: "",
  slug: "",
  description: "",
  logo_url: "",
  banner_url: "",
  phone: "",
  whatsapp: "",
  email: "",
  address: "",
  work_hours: "",
  city: "",
  category: "",
  delivery: [],
};

function ImagePicker({
  label,
  value,
  aspect,
  userId,
  onChange,
}: {
  label: string;
  value: string;
  aspect: string;
  userId: string;
  onChange: (url: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadListingPhoto(userId, file));
      toast.success("Şəkil yükləndi");
    } catch (err) {
      toast.error(azError(err, "Şəkil yüklənmədi"));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className={`mt-2 flex w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-border text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary ${aspect}`}
      >
        {value ? (
          <img src={value} alt={label} className="size-full object-cover" />
        ) : busy ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <span className="flex items-center gap-2">
            <ImageIcon className="size-4" /> Şəkil seç
          </span>
        )}
      </button>
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="mt-1 text-xs text-muted-foreground hover:text-destructive"
        >
          Şəkli sil
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}

function MyStorePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<StoreInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: store, isLoading } = useQuery({
    queryKey: ["my-store-full", user?.id],
    queryFn: () => fetchMyStore(user!.id),
    enabled: Boolean(user?.id),
  });

  const { data: myAds } = useQuery({
    queryKey: ["my-listings", user?.id],
    queryFn: () => fetchMyListings(user!.id),
    enabled: Boolean(user?.id),
  });

  const { data: followers = 0 } = useQuery({
    queryKey: ["store-followers", store?.id],
    queryFn: () => fetchFollowerCount(store!.id),
    enabled: Boolean(store?.id),
  });

  const { data: reviews } = useQuery({
    queryKey: ["store-reviews", store?.id],
    queryFn: () => fetchStoreReviews(store!.id),
    enabled: Boolean(store?.id),
  });

  const removeAd = async (id: string) => {
    if (!window.confirm("Məhsulu silmək istədiyinizə əminsiniz?")) return;
    const { error } = await supabase.from("listings").delete().eq("id", id);
    if (error) {
      toast.error(azError(error, "Silinmədi"));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-listings", user?.id] });
    toast.success("Məhsul silindi");
  };

  const archiveAd = async (id: string, status: string) => {
    const next = status === "active" ? "draft" : "active";
    const { error } = await supabase.from("listings").update({ status: next }).eq("id", id);
    if (error) {
      toast.error(azError(error, "Status dəyişmədi"));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["my-listings", user?.id] });
    toast.success(next === "active" ? "Məhsul dərc olundu" : "Məhsul arxivləndi");
  };

  useEffect(() => {
    if (!store) return;
    setForm({
      name: store.name ?? "",
      slug: store.slug ?? "",
      description: store.description ?? "",
      logo_url: store.logo_url ?? "",
      banner_url: store.banner_url ?? "",
      phone: store.phone ?? "",
      whatsapp: store.whatsapp ?? "",
      email: store.email ?? "",
      address: store.address ?? "",
      work_hours: store.work_hours ?? "",
      city: store.city ?? "",
      category: store.category ?? "",
      delivery: store.delivery ?? [],
    });
  }, [store]);

  if (!user) return null;

  const set = (key: keyof StoreInput, value: string | string[]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleDelivery = (value: string) =>
    setForm((prev) => ({
      ...prev,
      delivery: prev.delivery.includes(value)
        ? prev.delivery.filter((d) => d !== value)
        : [...prev.delivery, value],
    }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Mağaza adını yazın");
      return;
    }
    setSaving(true);
    try {
      await saveStore(user.id, form, store?.id);
      await queryClient.invalidateQueries({ queryKey: ["my-store-full", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["my-store", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["stores", "active"] });
      toast.success(store ? "Mağaza yeniləndi" : "Mağaza yaradıldı — admin təsdiqini gözləyir");
    } catch (err) {
      toast.error(azError(err, "Mağaza yadda saxlanılmadı"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-x py-5 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight sm:text-2xl">Mağazam</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Mağaza profilini, əlaqə məlumatlarını və məhsullarını idarə et.
          </p>
        </div>
        {store && (
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-semibold">
              {storeStatusLabels[store.status] ?? store.status}
            </span>
            {store.verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-info/10 px-3 py-1 text-xs font-semibold text-info">
                <BadgeCheck className="size-3.5" /> Təsdiqlənib
              </span>
            )}
            <Link to="/magaza/$slug" params={{ slug: storeSlug(store) }}>
              <Button variant="outline" size="sm" className="rounded-full">
                <ExternalLink className="size-4" /> Səhifəm
              </Button>
            </Link>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="mt-6 h-64 animate-pulse rounded-3xl bg-muted" />
      ) : (
        <form onSubmit={submit} className="mt-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-4 rounded-3xl border border-border bg-card p-5 shadow-card sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="store-name">Mağaza adı *</Label>
                <Input
                  id="store-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Məs: TexnoMarket"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="store-slug">Qısa ünvan (albazar.az/magaza/...)</Label>
                <Input
                  id="store-slug"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="texnomarket"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="store-desc">Təsvir</Label>
                <Textarea
                  id="store-desc"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  rows={4}
                  placeholder="Mağazanız, məhsullarınız və çatdırılma şərtləri haqqında qısa məlumat"
                  className="mt-2 rounded-2xl"
                />
              </div>
              <div>
                <Label htmlFor="store-phone">Telefon</Label>
                <Input
                  id="store-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+994 50 000 00 00"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div>
                <Label htmlFor="store-wa">WhatsApp</Label>
                <Input
                  id="store-wa"
                  value={form.whatsapp}
                  onChange={(e) => set("whatsapp", e.target.value)}
                  placeholder="994500000000"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div>
                <Label htmlFor="store-email">E-poçt</Label>
                <Input
                  id="store-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="info@magaza.az"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div>
                <Label htmlFor="store-city">Şəhər</Label>
                <Select value={form.city} onValueChange={(v) => set("city", v)}>
                  <SelectTrigger id="store-city" className="mt-2 h-11! rounded-2xl">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {cities.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="store-category">Kateqoriya</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger id="store-category" className="mt-2 h-11! rounded-2xl">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {categories.map((c) => (
                      <SelectItem key={c.slug} value={c.name}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="store-address">Ünvan</Label>
                <Input
                  id="store-address"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Nizami küç. 10"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div>
                <Label htmlFor="store-hours">İş saatları</Label>
                <Input
                  id="store-hours"
                  value={form.work_hours}
                  onChange={(e) => set("work_hours", e.target.value)}
                  placeholder="B.e–Şənbə 10:00–19:00"
                  className="mt-2 h-11 rounded-2xl"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Çatdırılma növləri</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DELIVERY_OPTIONS.map((d) => {
                    const on = form.delivery.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleDelivery(d.value)}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                          on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-card text-muted-foreground hover:border-primary"
                        }`}
                      >
                        <Truck className="mr-1 inline size-3.5" /> {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="h-11 w-full rounded-full font-semibold sm:w-auto sm:px-8"
            >
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {store ? "Yadda saxla" : "Mağaza yarat"}
            </Button>
            {!store && (
              <p className="text-xs text-muted-foreground">
                Mağaza yaradıldıqdan sonra admin təsdiqindən keçir və sonra saytda görünür.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-4 rounded-3xl border border-border bg-card p-5 shadow-card">
              <ImagePicker
                label="Loqo"
                value={form.logo_url}
                aspect="aspect-square max-w-[160px]"
                userId={user.id}
                onChange={(v) => set("logo_url", v)}
              />
              <ImagePicker
                label="Banner"
                value={form.banner_url}
                aspect="aspect-[3/1]"
                userId={user.id}
                onChange={(v) => set("banner_url", v)}
              />
            </div>

            {store && (
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-card">
                  <Users className="mx-auto size-4 text-primary" />
                  <p className="mt-1 text-lg font-black">{followers}</p>
                  <p className="text-[11px] text-muted-foreground">İzləyici</p>
                </div>
                <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-card">
                  <Eye className="mx-auto size-4 text-info" />
                  <p className="mt-1 text-lg font-black">
                    {(myAds ?? []).reduce((s, a) => s + (a.views ?? 0), 0)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Baxış</p>
                </div>
                <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-card">
                  <Star className="mx-auto size-4 fill-premium text-premium" />
                  <p className="mt-1 text-lg font-black">
                    {reviews?.length ? averageRating(reviews).toFixed(1) : "—"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{reviews?.length ?? 0} rəy</p>
                </div>
                <div className="rounded-2xl border border-border bg-card p-3 text-center shadow-card">
                  <StoreIcon className="mx-auto size-4 text-primary" />
                  <p className="mt-1 text-lg font-black">{myAds?.length ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Məhsul</p>
                </div>
                <Link
                  to="/mesajlar"
                  search={{ user: "" }}
                  className="rounded-2xl border border-border bg-card p-3 text-center shadow-card"
                >
                  <MessageSquare className="mx-auto size-4 text-primary" />
                  <p className="mt-1 text-lg font-black">—</p>
                  <p className="text-[11px] text-muted-foreground">Mesajlar</p>
                </Link>
                <div className="rounded-2xl border border-dashed border-border bg-card p-3 text-center">
                  <Package className="mx-auto size-4 text-muted-foreground" />
                  <p className="mt-1 text-[11px] font-bold">Sifarişlər</p>
                  <p className="text-[11px] text-muted-foreground">Tezliklə</p>
                </div>
              </div>
            )}

            <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-bold">
                  <StoreIcon className="size-4 text-primary" /> Məhsullarım
                </h2>
                <Link to="/yeni-elan">
                  <Button size="sm" variant="outline" className="rounded-full">
                    Yeni məhsul
                  </Button>
                </Link>
              </div>
              <div className="mt-3 space-y-2">
                {(myAds ?? []).map((ad) => (
                  <div key={ad.id} className="rounded-2xl border border-border p-2.5">
                    <div className="flex items-center gap-3">
                      <img
                        src={ad.images[0] ?? PLACEHOLDER_IMAGE}
                        alt={ad.title}
                        loading="lazy"
                        className="size-12 shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{ad.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {statusLabels[ad.status] ?? ad.status} • {ad.views} baxış
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Link to="/elan-redakte/$id" params={{ id: ad.id }}>
                        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs">
                          <Pencil className="size-3.5" /> Redaktə
                        </Button>
                      </Link>
                      <Link to="/elan-reklam/$id" params={{ id: ad.id }} search={{ plan: "vip" as const, days: 7 }}>
                        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs">
                          <Crown className="size-3.5" /> VIP et
                        </Button>
                      </Link>
                      <Link to="/elan-reklam/$id" params={{ id: ad.id }} search={{ plan: "premium" as const, days: 7 }}>
                        <Button size="sm" variant="outline" className="h-8 rounded-full text-xs">
                          <Sparkles className="size-3.5" /> Premium et
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-full text-xs"
                        onClick={() => void archiveAd(ad.id, ad.status)}
                      >
                        <Archive className="size-3.5" />
                        {ad.status === "active" ? "Arxivlə" : "Dərc et"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-full text-xs text-destructive"
                        onClick={() => void removeAd(ad.id)}
                      >
                        <Trash2 className="size-3.5" /> Sil
                      </Button>
                    </div>
                  </div>
                ))}
                {!myAds?.length && (
                  <p className="text-sm text-muted-foreground">Hələ məhsul yoxdur.</p>
                )}
              </div>
            </div>

            {store?.id && user?.id && (
              <div className="grid gap-4">
                <StoreOrderForm
                  storeId={store.id}
                  ownerId={user.id}
                  storeName={store.name}
                  ownerMode
                />
                <StoreOrdersList storeId={store.id} />
                <StoreMediaManager storeId={store.id} ownerId={user.id} />
                <StoreVideoManager storeId={store.id} ownerId={user.id} />

              </div>
            )}

            <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
              <h2 className="flex items-center gap-2 font-bold">
                <Sparkles className="size-4 text-premium" /> Mağaza paketləri
              </h2>
              <div className="mt-3 space-y-2">
                {STORE_PLANS.map((p) => {
                  const active = (store?.plan ?? "basic") === p.key;
                  return (
                    <div
                      key={p.key}
                      className={`rounded-2xl border p-3 ${
                        active ? "border-primary bg-accent" : "border-border"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold">{p.name}</p>
                        <span className="text-xs font-semibold text-primary">{p.price}</span>
                      </div>
                      <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                        {p.perks.map((perk) => (
                          <li key={perk}>• {perk}</li>
                        ))}
                      </ul>
                      {active ? (
                        <span className="mt-2 inline-block rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
                          Aktiv paket
                        </span>
                      ) : (
                        <a
                          href={`https://wa.me/994776030335?text=${encodeURIComponent(
                            `Salam, ${p.name} paketini almaq istəyirəm. Mağaza: ${form.name || "-"}`,
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-block"
                        >
                          <Button size="sm" className="h-8 rounded-full text-xs">
                            Paketi al
                          </Button>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}